/**
 * smPinRouting — which pins a State Machine transition should hang off
 * (ENG-1072).
 *
 * A state node carries four pins instead of the old single left-in / right-out
 * pair: two OUTPUTS (left and right, drawn as triangles at the bottom corners)
 * and two INPUTS (top and bottom, drawn as circles). A transition picks one of
 * each from the live geometry of the two nodes it joins:
 *
 *   - it leaves through the RIGHT output when the destination is to the right,
 *     and flips to the LEFT output when the destination is to the left;
 *   - it enters through the destination's TOP input when the destination sits
 *     below the source, and flips to its BOTTOM input when it sits above.
 *
 * The two axes are decided independently, so a destination that is up-and-left
 * gets the left output and the bottom input.
 *
 * This is a pure function of geometry, deliberately kept out of the render map
 * in StateMachineEditor: nothing here is persisted. The choice is recomputed
 * every render from `node.position` + `node.measured`, which is what makes the
 * splines flip live while a node is being dragged, and which means a blueprint
 * saved before this change needs no migration — whatever handle ids its edges
 * carry are simply overridden.
 */

/** Handle ids rendered by StateNode / EntryNode. */
export const SM_PINS = {
  outLeft:  'out-l',
  outRight: 'out-r',
  inTop:    'in-t',
  inBottom: 'in-b',
}

/**
 * Geometry of a ReactFlow node as {x, y, width, height}, or null when the node
 * is missing or has no position.
 *
 * Pass an INTERNAL node (`getInternalNode(id)`), not one out of the `nodes`
 * array you hand to `<ReactFlow>`: only the internal node reliably carries
 * `measured`, and its `internals.positionAbsolute` is already resolved against
 * any parent. A user-facing node still works — position falls back to
 * `node.position` — but its size is usually absent, and then the comparison
 * degrades to top-left corners. That is deterministic rather than wrong, and it
 * self-corrects the moment sizes arrive.
 */
export function nodeRect(node) {
  const position = node?.internals?.positionAbsolute ?? node?.position
  if (!position) return null
  return {
    x: position.x,
    y: position.y,
    width:  node.measured?.width  ?? node.width  ?? 0,
    height: node.measured?.height ?? node.height ?? 0,
  }
}

/** True once a node carries a real measured size, so its centre is knowable. */
export function isMeasured(node) {
  return !!(node?.measured?.width && node?.measured?.height)
}

/**
 * Pick the source and target handle ids for a transition between two rects.
 *
 * Comparison is centre-to-centre so a wide node and a narrow one are judged by
 * where they actually sit, not by their top-left corners. Exact ties resolve to
 * the right output and the top input, so a perfectly aligned pair settles on
 * one answer rather than flickering between two equally valid ones.
 */
export function pickPins(source, target) {
  const sourceCenterX = source.x + source.width  / 2
  const sourceCenterY = source.y + source.height / 2
  const targetCenterX = target.x + target.width  / 2
  const targetCenterY = target.y + target.height / 2

  return {
    sourceHandle: targetCenterX >= sourceCenterX ? SM_PINS.outRight : SM_PINS.outLeft,
    targetHandle: targetCenterY <  sourceCenterY ? SM_PINS.inBottom : SM_PINS.inTop,
  }
}
